import { useProject } from "@/common";
import styled from "@emotion/styled";
import Avatar from "@imminently/decisively-ui/Avatar";
import Icon from "@imminently/decisively-ui/Icon";
import Select from "@imminently/decisively-ui/Select";
import Skeleton from "@mui/material/Skeleton";
import { useDispatch } from "react-redux";

import { projectService } from "@/app/providers/services";
import { ProjectGuard } from "@/auth/ComponentGuard";
import { ImmiCombobox } from "@/components/radix/UserSelect/AsyncSelect";
import { useRouting } from "@/hooks/use-routing";
import { useAddProjectModal } from "@/modals/ProjectModal";
import { ReleaseSelector } from "@/ui/blocks/ReleaseSelector";
import { useTranslation } from "react-i18next";
import SearchDocuments from "../components/search/search-documents/SearchDocuments";
import { setProject, useScope } from "../redux/scope";

// TODO: fix up this into a nicer common style instead of exporting later -- JW
export const ScopeLoading = styled(Skeleton)`
  border-radius: 0.5rem;
`;

const Container = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 1rem;
  align-items: center;
`;

/**
 * @deprecated Use `ProjectSelector` from `@/ui/blocks/ProjectSelector` instead.
 */
export const ProjectSelector = () => {
  const dispatch = useDispatch();
  const openAddProject = useAddProjectModal();
  const { goToPage } = useRouting();
  const { workspace, project: projectId } = useScope();
  const project = useProject(projectId);
  const { t } = useTranslation();

  const comboProps = {
    label: t("projects_label"),
    service: projectService,
    filter: {
      workspace,
      sort: { field: "created", order: "desc" } as const,
    },
    value: projectId,
    onSelect: (item) => dispatch(setProject(item.id, { resetRelease: true })),
    renderItem: (item) => (
      <>
        <Avatar size="sm">{item.name.slice(0, 1)}</Avatar>
        <span className="block truncate">{item.name}</span>
      </>
    ),
    actions: {
      primary: {
        name: t("browse_all"),
        icon: <Icon name="browse-all" />,
        type: "button" as const,
        intent: "secondary" as const,
        variant: "ghost" as const,
        size: "sm" as const,
        onClick: () => {
          goToPage("/projects");
        },
      },
      secondary: [
        {
          name: t("projects.create_label"),
          icon: <Icon name="add" />,
          type: "button" as const,
          variant: "ghost" as const,
          size: "sm" as const,
          onClick: () => {
            openAddProject();
          },
        },
      ],
    },
  };

  return (
    <ImmiCombobox {...comboProps}>
      <div className="w-[15rem]">
        <Select
          value={project?.name ? String(project.name) : null}
          options={project?.name ? [{ value: String(project.name), label: String(project.name) }] : []}
          placeholder={t("projects_label")}
          searchable={false}
          clearable={false}
          menuIsOpen={false}
          openMenuOnClick={false}
          openMenuOnFocus={false}
          onValueChange={() => {}}
          className="w-full text-left"
        />
      </div>
      {/* <button className="flex items-center gap-2">
        <Cylinder className="w-4 h-4" />
        <span>{project?.name}</span>
        <ChevronsUpDown className="w-4 h-4" />
      </button> */}
    </ImmiCombobox>
  );
};

export type ScopeProps = React.PropsWithChildren<{
  hideSearch?: boolean;
  childrenBeforeSearch?: boolean;
  releaseSelector?: React.ReactNode;
  hideReleaseSelector?: boolean;
}>;

export const Scope = ({
  hideSearch,
  children,
  childrenBeforeSearch,
  releaseSelector,
  hideReleaseSelector,
}: ScopeProps) => {
  const { project: projectId, release: releaseId } = useScope();
  const project = useProject(projectId);

  return (
    <Container>
      {project ? (
        <ProjectSelector />
      ) : (
        <ScopeLoading
          variant="rectangular"
          width="15rem"
          height="2.5rem"
        />
      )}

      {hideReleaseSelector ? null : project ? (
        (releaseSelector ?? <ReleaseSelector />)
      ) : (
        <ScopeLoading
          variant="rectangular"
          width="15rem"
          height="2.5rem"
        />
      )}

      {childrenBeforeSearch ? children : null}

      {hideSearch ? null : (
        <ProjectGuard>
          <SearchDocuments key={releaseId || ""} />
        </ProjectGuard>
      )}

      {childrenBeforeSearch ? null : children}
    </Container>
  );
};

export const MonitorProjectScope = (props: Omit<ScopeProps, "releaseSelector" | "hideReleaseSelector">) => (
  <Scope
    {...props}
    hideReleaseSelector
  />
);
